<script lang="ts" setup>
import type { NavItem } from '~kui/types/navigation';

import { NuxtLink } from '#components';
import { Button } from '~ds/components/ui/button';
import { ICONS } from '~ds/lib/icons';
import { useUnifiedPublicLoaderContext } from '~kui/composables/useUnifiedPublicLoaderContext';

interface PublicNavItem extends NavItem {
  isSecondary?: boolean;

}

/**
 * The public site's top bar: the logo, the links in a pill, and the sign-in. `md` is the compact
 * bar with the sign-in inside the pill. `lg` sets the links at body size with room between them
 * and stands the sign-in beside the pill as a button of its own.
 *
 * The logo is handed to the public loader's context while the bar is mounted, because the intro
 * animation flies its own logo into exactly where this one sits. A ref rather than an id: a page
 * can draw this bar more than once (the coach draws it above the chat and again over a subpage),
 * and an id would then be in the document twice.
 */
interface Props {
  navItems: PublicNavItem[];
  homeLink?: string;
  logoLabel: string;
  logoIllustration?: 'anyway-logo' | 'anyway-logo-beta';
  /**
   * Keep the bar at its full height but show only the logo, the way the authed layout does.
   * For a page whose visitor has just signed in mid-flow: the links no longer apply, and
   * dropping the bar would shift everything below it.
   */
  logoOnly?: boolean;
  /** The signed-in visitor's in-app motion switch, on top of the OS preference. */
  animationsEnabled?: boolean;
  size?: 'md' | 'lg';
}

const props = withDefaults(defineProps<Props>(), {
  homeLink: '',
  logoIllustration: 'anyway-logo-beta',
  logoOnly: false,
  animationsEnabled: true,
  size: 'md',
});

const isNavOpen = ref(false);

const primaryNavItems = computed(() => props.navItems.filter(item => !item.isSecondary));
const secondaryNavItem = computed(() => props.navItems.find(item => item.isSecondary));
const hasPrimaryNavItems = computed(() => primaryNavItems.value.length > 0);
// At `lg` the sign-in leaves the pill to stand beside it.
const pillNavItems = computed(() => (props.size === 'lg' ? primaryNavItems.value : props.navItems));

// At `lg` the pill, the Sign in beside it and a student's stat pills share one height, so the bar
// reads the same signed in or out.
const pillClasses = computed(() => twMerge(cva('flex items-center gap-4 rounded-full', {
  variants: {
    size: {
      md: 'self-stretch',
      lg: 'h-12.5',
    },
    filled: {
      true: 'lg:bg-white p-1',
      false: '',
    },
  },
  compoundVariants: [{ size: 'lg', filled: true, class: 'px-2' }],
})({ size: props.size, filled: hasPrimaryNavItems.value })));

// The current page's link is set in the heavier weight; `after` reserves that width on every link
// so the pill does not move as the weight changes. At `md` the router marks the exact route; at `lg`
// the item's own isActive does.
const linkVariants = cva('relative px-4 py-2 bg-transparent transition-all duration-300 hover:bg-secondary-subtle rounded-full cursor-pointer text-nowrap after:content-[attr(data-text)] after:block after:h-0 after:overflow-hidden after:invisible after:pointer-events-none', {
  variants: {
    size: {
      md: 'text-sm font-medium after:font-semibold',
      lg: 'text-base after:font-medium',
    },
    active: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    { size: 'lg', active: false, class: 'font-normal' },
    { size: 'lg', active: true, class: 'font-medium' },
  ],
});

const linkClasses = (active: boolean) => twMerge(linkVariants({ size: props.size, active }));

const drawerNavItems = computed(() => primaryNavItems.value.map(item => ({
  ...item,
  imagePath: item.imagePath ?? '/images/illustrations/rocket-blue.webp',
})));

const closeNav = () => {
  isNavOpen.value = false;
};

const handleItemClick = (item: NavItem) => {
  closeNav();
  if (item.onClick) {
    void item.onClick();
  }
};

const loaderCtx = useUnifiedPublicLoaderContext();
const navVisible = computed(() => !loaderCtx || loaderCtx.loaderComplete.value);

const logo = useTemplateRef<{ $el: Element | null }>('logo');
const logoElement = (): Element | null => (logo.value?.$el instanceof Element ? logo.value.$el : null);

onMounted(() => {
  if (loaderCtx && !loaderCtx.logoRef.value) {
    loaderCtx.logoRef.value = logoElement();
  }
});

onBeforeUnmount(() => {
  if (loaderCtx && loaderCtx.logoRef.value === logoElement()) {
    loaderCtx.logoRef.value = null;
  }
});
</script>

<template>
  <div
    class="px-2 pt-4 mb-4 w-full lg:bg-transparent transition-opacity duration-500"
    :class="[
      navVisible ? 'opacity-100' : 'opacity-0',
      hasPrimaryNavItems ? 'lg:mb-0 lg:px-0 lg:pt-0' : '',
    ]"
  >
    <header
      :class="twMerge(
        'w-full max-w-7xl mx-auto rounded-full transition-all duration-300 delay-300 bg-white',
        hasPrimaryNavItems ? 'px-4 py-2 lg:py-8 lg:rounded-none lg:bg-transparent' : 'px-6 py-3',
      )"
    >
      <div
        class="flex justify-between  "
      >
        <component
          :is="logoOnly ? 'span' : NuxtLink"
          v-bind="logoOnly ? {} : { 'to': homeLink, 'aria-label': logoLabel }"
          data-testid="public-nav-logo"
        >
          <KUICoreIllustration
            ref="logo"
            :illustration="logoIllustration"
            class="h-9 shrink-0 text-foreground"
            alt="Logo"
          />
        </component>
        <div v-if="!logoOnly" class="hidden items-center gap-4 lg:flex">
          <nav
            :aria-label="$t('unified.nav.mainAriaLabel')"
            :class="pillClasses"
          >
            <template
              v-for="item in pillNavItems"
              :key="item.label"
            >
              <slot v-if="item.isSecondary" name="secondary">
                <NuxtLink
                  :to="item.to"
                  :data-text="item.label"
                  class="px-4 py-2 text-sm font-semibold bg-secondary rounded-full text-nowrap cursor-pointer"
                >
                  {{ item.label }}
                </NuxtLink>
              </slot>
              <NuxtLink
                v-else
                :to="item.to"
                :data-text="item.label"
                :class="linkClasses(size === 'lg' && Boolean(item.isActive))"
                v-bind="size === 'lg'
                  ? (item.isActive ? { 'aria-current': 'page' } : {})
                  : { 'exact-active-class': 'font-semibold' }"
              >
                {{ item.label }}
              </NuxtLink>
            </template>
          </nav>
          <template v-if="size === 'lg' && secondaryNavItem">
            <slot name="secondary">
              <KUICoreClickable
                :to="secondaryNavItem.to"
                class="inline-flex h-12.5 items-center rounded-full bg-secondary px-6 text-base font-semibold text-nowrap text-secondary-foreground transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                data-testid="public-nav-sign-in"
                @click="handleItemClick(secondaryNavItem)"
              >
                {{ secondaryNavItem.label }}
              </KUICoreClickable>
            </slot>
          </template>
          <slot name="actions" />
        </div>
        <div v-if="!logoOnly" class="flex items-center gap-2 lg:hidden">
          <slot v-if="$slots.secondary" name="secondary" />
          <NuxtLink
            v-else-if="!hasPrimaryNavItems && secondaryNavItem"
            :to="secondaryNavItem.to"
            class="px-4 py-2 text-sm font-semibold bg-secondary rounded-full text-nowrap cursor-pointer"
            @click="handleItemClick(secondaryNavItem)"
          >
            {{ secondaryNavItem.label }}
          </NuxtLink>
          <KUIUnifiedNavigationDrawer
            v-if="hasPrimaryNavItems"
            v-model:open="isNavOpen"
            :nav-items="drawerNavItems"
            :animations-enabled="animationsEnabled"
            @item-click="closeNav"
          >
            <template #trigger>
              <KUICoreButtonIcon
                :icon="ICONS.menuSolid"
                variant="white"
                :aria-label="$t('unified.nav.menuButton')"
              />
            </template>
            <Button
              v-if="secondaryNavItem && !$slots.secondary"
              variant="secondary"
              class="mx-auto"
              size="lg"
              as-child
              @click="handleItemClick(secondaryNavItem)"
            >
              <NuxtLink :to="secondaryNavItem.to">
                {{ secondaryNavItem.label }}
              </NuxtLink>
            </Button>
            <slot name="drawer" :close="closeNav" />
          </KUIUnifiedNavigationDrawer>
        </div>
      </div>
    </header>
  </div>
</template>
